// Hook de datos del copiloto Jido — POST /api/jido/chat (jido-agent F2).
//
// No hay `useQuery`/cache acá: el historial de la conversación vive en
// estado local del componente (`CopilotoChat`), no en el cache de TanStack
// Query — no hay necesidad de invalidar nada ni de persistirlo entre
// pantallas en este MVP (sin streaming, request/response simple).

import { useMutation } from '@tanstack/react-query';
import { jidoChatRequestSchema, jidoChatResponseSchema, type JidoChatRequest } from 'schemas';
import { ApiError, apiPost } from '../../lib/api/client';

// Estados HTTP transitorios del proxy/gateway (no de la app): el reverse-proxy
// a veces resetea una conexión keepalive stale hacia Node y devuelve un 502
// intermitente. Reintentar en una conexión nueva lo resuelve.
const ESTADOS_TRANSITORIOS = new Set([502, 503, 504]);

export function useCopiloto() {
  return useMutation({
    mutationFn: (input: JidoChatRequest) => {
      // Validación client-side (espejo del backend) — falla rápido sin roundtrip.
      const parsed = jidoChatRequestSchema.parse(input);
      return apiPost('/api/jido/chat', parsed, jidoChatResponseSchema);
    },
    // Las QUERIES de TanStack reintentan por default, pero las MUTATIONS no —
    // por eso Jido era el único que mostraba "No pudimos enviar" ante un 502
    // intermitente del proxy. Reintentamos hasta 2 veces ante 502/503/504 o un
    // error de red (fetch reject = TypeError), pero NO ante errores de
    // validación (ZodError) ni de auth/permite (401/403/4xx), donde reintentar
    // no cambia nada. Una pregunta read-only es segura de reintentar.
    retry: (failureCount, error) => {
      if (failureCount >= 2) return false;
      if (error instanceof ApiError) return ESTADOS_TRANSITORIOS.has(error.status);
      return error instanceof TypeError; // error de red, no ZodError
    },
    retryDelay: 500,
  });
}
